import { AgentsListTab, AgentFormState, BuilderMode, BuilderActiveTab, AgentsPanelType } from '@trovata/app/features/ai-agents/models/agents.model';

// ==========================================
// Lifecycle Actions
// ==========================================

/** Initializes the agents state with default values. */
export class InitAgentsState {
	static readonly type: string = '[Agents] InitAgentsState';
}

/** Clears all agents state data. */
export class ClearAgentsState {
	static readonly type: string = '[Agents] ClearAgentsState';
}

/** Resets the cache flag then re-dispatches InitAgentsState to refetch the list. */
export class ResetAgentsState {
	static readonly type: string = '[Agents] ResetAgentsState';
}

// ==========================================
// Agents List Actions
// ==========================================

/** Fetches the list of agents from the API. */
export class GetAgents {
	static readonly type: string = '[Agents] GetAgents';
}

/** Selects an agent by ID, or deselects if null. */
export class SelectAgent {
	static readonly type: string = '[Agents] SelectAgent';
	constructor(public agentId: string | null) {}
}

/** Sets the active tab on the agents list page. */
export class SetAgentsListTab {
	static readonly type: string = '[Agents] SetAgentsListTab';
	constructor(public tab: AgentsListTab) {}
}

// ==========================================
// Chat Session Actions
// ==========================================

/** Starts a new chat session for the selected agent. */
export class StartChatSession {
	static readonly type: string = '[Agents] StartChatSession';
}

/** Sends a chat message to the current agent session. */
export class SendChatMessage {
	static readonly type: string = '[Agents] SendChatMessage';
	constructor(public prompt: string) {}
}

/** Stops the current AI response generation. */
export class StopChatGeneration {
	static readonly type: string = '[Agents] StopChatGeneration';
}

/** Clears `chatError` without running any other side effect. */
export class ClearChatError {
	static readonly type: string = '[Agents] ClearChatError';
}

/** Clears the current chat session and messages. */
export class ClearChatSession {
	static readonly type: string = '[Agents] ClearChatSession';
}

// ==========================================
// Agent CRUD Actions
// ==========================================

/** Loads a single agent with its full chat history. */
export class LoadAgentWithHistory {
	static readonly type: string = '[Agents] LoadAgentWithHistory';
	constructor(public agentId: string) {}
}

/**
 * Saves an agent via the PATCH API.
 *
 * The builder's current `configForm.triggerConfig` is mapped to the API's
 * `ruleConfig` field (may be `null` when no trigger is configured).
 *
 * `paused` is optional: undefined leaves the flag untouched on the backend,
 * `false` activates / unpauses, `true` pauses an active agent.
 */
export class SaveAgent {
	static readonly type: string = '[Agents] SaveAgent';
	constructor(
		public agentId: string,
		public name: string,
		public prompt: string,
		public paused?: boolean
	) {}
}

/**
 * Toggles the `paused` flag on a saved agent.
 *
 * Distinct from `SaveAgent` because the listing-context Pause/Resume toggle
 * has no `configForm` state to serialize — only the paused flag is sent in
 * the PATCH body, leaving `name`, `prompt`, and `ruleConfig` untouched.
 */
export class SetAgentPaused {
	static readonly type: string = '[Agents] SetAgentPaused';
	constructor(
		public agentId: string,
		public paused: boolean
	) {}
}

/** Deletes an agent by ID. */
export class DeleteAgent {
	static readonly type: string = '[Agents] DeleteAgent';
	constructor(public agentId: string) {}
}

/** Duplicates an existing agent by ID. */
export class DuplicateAgent {
	static readonly type: string = '[Agents] DuplicateAgent';
	constructor(public agentId: string) {}
}

// ==========================================
// Builder Dialog Lifecycle Actions
// ==========================================

/** Opens the agent builder dialog in the specified mode. */
export class OpenBuilderDialog {
	static readonly type: string = '[Agents] OpenBuilderDialog';
	constructor(
		public mode: BuilderMode,
		public agentId?: string
	) {}
}

/** Closes the agent builder dialog. */
export class CloseBuilderDialog {
	static readonly type: string = '[Agents] CloseBuilderDialog';
}

/** Sets the active tab within the builder dialog. */
export class SetBuilderActiveTab {
	static readonly type: string = '[Agents] SetBuilderActiveTab';
	constructor(public tab: BuilderActiveTab) {}
}

// ==========================================
// Config Form Actions
// ==========================================

/** Updates the builder config form with partial changes. */
export class UpdateConfigForm {
	static readonly type: string = '[Agents] UpdateConfigForm';
	constructor(public changes: Partial<AgentFormState>) {}
}

// ==========================================
// Preview/Invoke Actions
// ==========================================

/** Previews the agent with the given prompt. */
export class PreviewAgent {
	static readonly type: string = '[Agents] PreviewAgent';
	constructor(
		public prompt: string,
		/** IANA timezone from the builder form; an unsaved agent has no schedule rule server-side to read it from. */
		public timezone?: string
	) {}
}

/** Invokes an active agent by ID. */
export class InvokeAgent {
	static readonly type: string = '[Agents] InvokeAgent';
	constructor(public agentId: string) {}
}

/** Polls for the result of a pending manual invocation. */
export class PollInvocationResult {
	static readonly type: string = '[Agents] PollInvocationResult';
	constructor(
		public agentId: string,
		public invocationId: string
	) {}
}

/**
 * Stops an in-flight invocation poll, e.g. when the user leaves the agents page.
 *
 * Distinct from the poll running out its budget: the run is not reported as
 * still processing, because the user chose to stop watching it.
 */
export class CancelInvocationPoll {
	static readonly type: string = '[Agents] CancelInvocationPoll';
}

/** Hides the "this run is still going" notice after the user acknowledges it. */
export class DismissInvocationPollNotice {
	static readonly type: string = '[Agents] DismissInvocationPollNotice';
}

// ==========================================
// Activity Actions
// ==========================================

/** Fetches the activity feed. */
export class GetActivityFeed {
	static readonly type: string = '[Agents] GetActivityFeed';
}

/** Sets the agent filter applied to the activity feed; empty array means no filter. */
export class SetActivityAgentFilter {
	static readonly type: string = '[Agents] SetActivityAgentFilter';
	constructor(public agentIds: readonly string[]) {}
}

/** Selects an activity entry by ID, or deselects if null. */
export class SelectActivity {
	static readonly type: string = '[Agents] SelectActivity';
	constructor(public activityId: string | null) {}
}

/** Fetches the full detail (including inline charts) for a single invocation. */
export class GetInvocationDetail {
	static readonly type: string = '[Agents] GetInvocationDetail';
	constructor(public invocationId: string) {}
}

/** Sets the selected panel type for the detail view. */
export class SetSelectedPanelType {
	static readonly type: string = '[Agents] SetSelectedPanelType';
	constructor(public panelType: AgentsPanelType | null) {}
}

// ==========================================
// Detail Panel Actions
// ==========================================

/** Opens the right-hand detail panel. */
export class OpenDetailPanel {
	static readonly type: string = '[Agents] OpenDetailPanel';
}

/** Closes the right-hand detail panel. */
export class CloseDetailPanel {
	static readonly type: string = '[Agents] CloseDetailPanel';
}
